import React, { useState, useEffect, useCallback } from 'react';

const CONTENT_TYPE_COLORS = {
  video: '#3b82f6',
  slide_deck: '#8b5cf6',
  content_panel: '#10b981',
  quiz_panel: '#f59e0b',
  reflection: '#ec4899',
  ai_lab: '#06b6d4',
  curated_resource: '#6366f1',
  prompt_canvas: '#14b8a6',
  module: '#f97316',
  scenario: '#a855f7',
  question: '#eab308',
  content_block: '#64748b',
  curriculum_track: '#0ea5e9',
  play: '#16a34a',
  drill: '#d97706',
};

const STATUS_STYLES = {
  pending: { border: '#f59e0b', bg: 'rgba(245,158,11,0.08)', label: 'Pending' },
  approved: { border: '#22c55e', bg: 'rgba(34,197,94,0.08)', label: 'Approved' },
  rejected: { border: '#ef4444', bg: 'rgba(239,68,68,0.08)', label: 'Rejected' },
  edited: { border: '#4f8ef7', bg: 'rgba(79,142,247,0.08)', label: 'Edited' },
  imported: { border: '#6366f1', bg: 'rgba(99,102,241,0.08)', label: 'From Library' },
};

const DIFFICULTY_COLORS = {
  beginner: '#4f8ef7',
  intermediate: '#f59e0b',
  advanced: '#ef4444',
};

// ── Inline styles (dark theme, matching coach portal) ─────────────────────────

const S = {
  card: (status) => {
    const st = STATUS_STYLES[status] || STATUS_STYLES.pending;
    return {
      background: '#1e2130',
      border: `1px solid ${st.border}`,
      borderRadius: 10,
      padding: 0,
      marginBottom: 12,
      overflow: 'hidden',
      fontFamily: 'inherit',
    };
  },
  cardHeader: (status) => {
    const st = STATUS_STYLES[status] || STATUS_STYLES.pending;
    return {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 16px',
      background: st.bg,
      borderBottom: `1px solid ${st.border}`,
    };
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#e2e8f0',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  statusBadge: (status) => {
    const st = STATUS_STYLES[status] || STATUS_STYLES.pending;
    return {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 8px',
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      background: st.bg,
      color: st.border,
      border: `1px solid ${st.border}`,
    };
  },
  cardBody: {
    padding: '12px 16px',
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 8,
    fontSize: 13,
    lineHeight: 1.5,
  },
  fieldLabel: {
    color: '#94a3b8',
    fontWeight: 600,
    minWidth: 100,
    flexShrink: 0,
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  fieldValue: {
    color: '#e2e8f0',
    flex: 1,
    wordBreak: 'break-word',
  },
  contentTypeBadge: (type) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 10px',
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 700,
    background: `${CONTENT_TYPE_COLORS[type] || '#6366f1'}22`,
    color: CONTENT_TYPE_COLORS[type] || '#6366f1',
    border: `1px solid ${CONTENT_TYPE_COLORS[type] || '#6366f1'}44`,
  }),
  difficultyBadge: (level) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 700,
    background: `${DIFFICULTY_COLORS[level] || '#94a3b8'}22`,
    color: DIFFICULTY_COLORS[level] || '#94a3b8',
  }),
  actions: {
    display: 'flex',
    gap: 8,
    padding: '10px 16px',
    borderTop: '1px solid #2d3748',
    background: 'rgba(15,17,23,0.5)',
    flexWrap: 'wrap',
  },
  btn: (bg, color, outline) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '6px 14px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    border: outline ? `1px solid ${bg}` : 'none',
    background: outline ? 'transparent' : bg,
    color: outline ? bg : color,
    transition: 'all 0.15s',
  }),
  preview: {
    background: '#0f1117',
    border: '1px solid #2d3748',
    borderRadius: 6,
    padding: '8px 12px',
    fontSize: 12,
    color: '#94a3b8',
    lineHeight: 1.5,
    maxHeight: 120,
    overflowY: 'auto',
    whiteSpace: 'pre-wrap',
    marginTop: 4,
  },
  expandBtn: {
    background: 'none',
    border: 'none',
    color: '#4f8ef7',
    fontSize: 12,
    cursor: 'pointer',
    padding: '4px 0',
    fontWeight: 500,
    textDecoration: 'underline',
    textUnderlineOffset: 2,
  },
  editInput: {
    background: '#0f1117',
    border: '1px solid #4f8ef7',
    borderRadius: 6,
    color: '#e2e8f0',
    padding: '7px 10px',
    fontSize: 13,
    width: '100%',
    outline: 'none',
    fontFamily: 'inherit',
  },
  editTextarea: {
    background: '#0f1117',
    border: '1px solid #4f8ef7',
    borderRadius: 6,
    color: '#e2e8f0',
    padding: '7px 10px',
    fontSize: 13,
    width: '100%',
    outline: 'none',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: 60,
  },
  choiceRow: (isCorrect) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '4px 8px',
    borderRadius: 4,
    background: isCorrect ? 'rgba(34,197,94,0.1)' : 'transparent',
    border: isCorrect ? '1px solid rgba(34,197,94,0.3)' : '1px solid transparent',
    marginBottom: 4,
    fontSize: 13,
    color: isCorrect ? '#22c55e' : '#e2e8f0',
  }),
  choiceMarker: (isCorrect) => ({
    width: 18,
    height: 18,
    borderRadius: '50%',
    border: `2px solid ${isCorrect ? '#22c55e' : '#2d3748'}`,
    background: isCorrect ? '#22c55e' : 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 1,
    fontSize: 10,
    color: '#fff',
    fontWeight: 700,
  }),
  scenarioListItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 10px',
    borderRadius: 6,
    background: 'rgba(15,17,23,0.6)',
    border: '1px solid #2d3748',
    marginBottom: 4,
    fontSize: 13,
    color: '#e2e8f0',
  },
};

// ── Type-specific artifact icon ───────────────────────────────────────────────

function artifactIcon(type) {
  const icons = {
    module: '📦',
    scenario: '🎬',
    question: '❓',
    content_block: '📄',
    slide_deck: '📊',
    curriculum_track: '🛤️',
    video: '🎥',
    quiz_panel: '🧪',
    ai_lab: '🔬',
    reflection: '💭',
    curated_resource: '📚',
    prompt_canvas: '✏️',
    content_panel: '📋',
    play: '🏈',
    drill: '🎯',
  };
  return icons[type] || '📎';
}

// ── Card body renderers per artifact_type ─────────────────────────────────────

function ModuleBody({ data, editing, draft, setDraft }) {
  const [expanded, setExpanded] = useState(false);
  if (editing) {
    return (
      <div>
        <div style={S.fieldRow}>
          <span style={S.fieldLabel}>Title</span>
          <input style={S.editInput} value={draft.title || ''} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
        </div>
        <div style={S.fieldRow}>
          <span style={S.fieldLabel}>Description</span>
          <textarea style={S.editTextarea} value={draft.description || ''} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
        </div>
        <div style={S.fieldRow}>
          <span style={S.fieldLabel}>Difficulty</span>
          <input style={S.editInput} value={draft.difficulty || ''} onChange={(e) => setDraft({ ...draft, difficulty: e.target.value })} />
        </div>
        <div style={S.fieldRow}>
          <span style={S.fieldLabel}>Mastery Gate</span>
          <input style={S.editInput} value={draft.mastery_gate ?? ''} onChange={(e) => setDraft({ ...draft, mastery_gate: e.target.value })} />
        </div>
      </div>
    );
  }
  const scenarios = data?.scenarios || [];
  return (
    <div>
      <div style={S.fieldRow}>
        <span style={S.fieldLabel}>Title</span>
        <span style={S.fieldValue}>{data?.title || '—'}</span>
      </div>
      <div style={S.fieldRow}>
        <span style={S.fieldLabel}>Description</span>
        <span style={S.fieldValue}>{data?.description || '—'}</span>
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
        <div style={S.fieldRow}>
          <span style={S.fieldLabel}>Scenarios</span>
          <span style={S.fieldValue}>{scenarios.length}</span>
        </div>
        {data?.difficulty && (
          <div style={S.fieldRow}>
            <span style={S.fieldLabel}>Difficulty</span>
            <span style={S.difficultyBadge(data.difficulty)}>{data.difficulty}</span>
          </div>
        )}
        {data?.mastery_gate !== undefined && (
          <div style={S.fieldRow}>
            <span style={S.fieldLabel}>Mastery Gate</span>
            <span style={S.fieldValue}>{data.mastery_gate}%</span>
          </div>
        )}
      </div>
      {scenarios.length > 0 && (
        <div>
          <button style={S.expandBtn} onClick={() => setExpanded(!expanded)}>
            {expanded ? '▾ Hide scenarios' : `▸ Show ${scenarios.length} scenario(s)`}
          </button>
          {expanded && (
            <div style={{ marginTop: 6 }}>
              {scenarios.map((sc, i) => (
                <div key={i} style={S.scenarioListItem}>
                  <span style={S.contentTypeBadge(sc.content_type)}>{sc.content_type?.replace(/_/g, ' ') || 'scene'}</span>
                  <span>{sc.title || sc.scene_id || `Scenario ${i + 1}`}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ScenarioBody({ data, editing, draft, setDraft }) {
  if (editing) {
    return (
      <div>
        <div style={S.fieldRow}>
          <span style={S.fieldLabel}>Title</span>
          <input style={S.editInput} value={draft.title || ''} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
        </div>
        <div style={S.fieldRow}>
          <span style={S.fieldLabel}>Content Type</span>
          <input style={S.editInput} value={draft.content_type || ''} onChange={(e) => setDraft({ ...draft, content_type: e.target.value })} />
        </div>
        <div style={S.fieldRow}>
          <span style={S.fieldLabel}>Difficulty</span>
          <input style={S.editInput} value={draft.difficulty || ''} onChange={(e) => setDraft({ ...draft, difficulty: e.target.value })} />
        </div>
      </div>
    );
  }
  const contentPreview = data?.content || data?.body || data?.prompt || '';
  return (
    <div>
      <div style={S.fieldRow}>
        <span style={S.fieldLabel}>Title</span>
        <span style={S.fieldValue}>{data?.title || '—'}</span>
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        {data?.content_type && (
          <div style={S.fieldRow}>
            <span style={S.fieldLabel}>Type</span>
            <span style={S.contentTypeBadge(data.content_type)}>{data.content_type.replace(/_/g, ' ')}</span>
          </div>
        )}
        {data?.difficulty && (
          <div style={S.fieldRow}>
            <span style={S.fieldLabel}>Difficulty</span>
            <span style={S.difficultyBadge(data.difficulty)}>{data.difficulty}</span>
          </div>
        )}
      </div>
      {contentPreview && (
        <div style={S.preview}>{typeof contentPreview === 'string' ? contentPreview.slice(0, 500) : JSON.stringify(contentPreview, null, 2).slice(0, 500)}</div>
      )}
    </div>
  );
}

function QuestionBody({ data, editing, draft, setDraft }) {
  if (editing) {
    return (
      <div>
        <div style={S.fieldRow}>
          <span style={S.fieldLabel}>Prompt</span>
          <textarea style={S.editTextarea} value={draft.prompt || ''} onChange={(e) => setDraft({ ...draft, prompt: e.target.value })} />
        </div>
        <div style={S.fieldRow}>
          <span style={S.fieldLabel}>Reasoning</span>
          <textarea style={S.editTextarea} value={draft.reasoning || ''} onChange={(e) => setDraft({ ...draft, reasoning: e.target.value })} />
        </div>
      </div>
    );
  }
  const choices = data?.choices || [];
  const correct = data?.correct_answer ?? data?.correct_index;
  return (
    <div>
      <div style={S.fieldRow}>
        <span style={S.fieldLabel}>Prompt</span>
        <span style={S.fieldValue}>{data?.prompt || data?.question || '—'}</span>
      </div>
      {choices.length > 0 && (
        <div style={{ marginTop: 6 }}>
          {choices.map((ch, i) => {
            const label = typeof ch === 'string' ? ch : (ch.text || ch.label || '');
            const isCorrect = correct === i || correct === label || ch.correct;
            return (
              <div key={i} style={S.choiceRow(isCorrect)}>
                <div style={S.choiceMarker(isCorrect)}>{isCorrect ? '✓' : ''}</div>
                <span>{label}</span>
              </div>
            );
          })}
        </div>
      )}
      {data?.reasoning && (
        <div style={{ ...S.fieldRow, marginTop: 8 }}>
          <span style={S.fieldLabel}>Reasoning</span>
          <span style={{ ...S.fieldValue, fontStyle: 'italic', color: '#94a3b8' }}>{data.reasoning}</span>
        </div>
      )}
    </div>
  );
}

function ContentBlockBody({ data, editing, draft, setDraft }) {
  const [expanded, setExpanded] = useState(false);
  if (editing) {
    return (
      <div>
        <div style={S.fieldRow}>
          <span style={S.fieldLabel}>Block ID</span>
          <input style={S.editInput} value={draft.block_id || ''} onChange={(e) => setDraft({ ...draft, block_id: e.target.value })} />
        </div>
        <div style={S.fieldRow}>
          <span style={S.fieldLabel}>Content</span>
          <textarea style={{ ...S.editTextarea, minHeight: 100 }} value={draft.content || draft.body || ''} onChange={(e) => setDraft({ ...draft, content: e.target.value })} />
        </div>
      </div>
    );
  }
  const body = data?.content || data?.body || data?.markdown || '';
  const truncated = typeof body === 'string' && body.length > 300;
  return (
    <div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        {data?.block_id && (
          <div style={S.fieldRow}>
            <span style={S.fieldLabel}>Block ID</span>
            <span style={{ ...S.fieldValue, fontFamily: 'monospace', fontSize: 12 }}>{data.block_id}</span>
          </div>
        )}
        {data?.difficulty && (
          <div style={S.fieldRow}>
            <span style={S.fieldLabel}>Difficulty</span>
            <span style={S.difficultyBadge(data.difficulty)}>{data.difficulty}</span>
          </div>
        )}
      </div>
      {body && (
        <div>
          <div style={S.preview}>
            {expanded || !truncated ? body : `${body.slice(0, 300)}…`}
          </div>
          {truncated && (
            <button style={S.expandBtn} onClick={() => setExpanded(!expanded)}>
              {expanded ? '▾ Collapse' : '▸ Show full content'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function SlideImagePlaceholder({ description }) {
  if (!description) return null;
  return (
    <div style={{
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      border: '1px dashed #4f8ef7',
      borderRadius: 6,
      padding: '12px 14px',
      marginTop: 8,
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 6,
        background: 'rgba(79,142,247,0.15)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, fontSize: 18,
      }}>
        🖼️
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#4f8ef7', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
          Suggested Visual
        </div>
        <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>
          {description}
        </div>
      </div>
    </div>
  );
}

function extractImageDescription(notes) {
  if (!notes || typeof notes !== 'string') return { imageDesc: '', otherNotes: '' };
  const imgPatterns = [
    /recommended image:\s*([\s\S]*?)(?:\.|$)/i,
    /image suggestion:\s*([\s\S]*?)(?:\.|$)/i,
    /suggested image:\s*([\s\S]*?)(?:\.|$)/i,
    /image:\s*([\s\S]*?)(?:\.\s|$)/i,
  ];
  for (const re of imgPatterns) {
    const m = notes.match(re);
    if (m && m[1] && m[1].trim().length > 20) {
      const imageDesc = m[1].trim();
      const otherNotes = notes.replace(m[0], '').trim();
      return { imageDesc, otherNotes };
    }
  }
  return { imageDesc: '', otherNotes: notes };
}

function SlideDeckBody({ data, editing, draft, setDraft }) {
  const [expanded, setExpanded] = useState(true);
  if (editing) {
    return (
      <div>
        <div style={S.fieldRow}>
          <span style={S.fieldLabel}>Title</span>
          <input style={S.editInput} value={draft.title || ''} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
        </div>
      </div>
    );
  }
  const slides = data?.slides || [];
  const visibleSlides = expanded ? slides : slides.slice(0, 2);
  return (
    <div>
      {data?.title && (
        <div style={S.fieldRow}>
          <span style={S.fieldLabel}>Title</span>
          <span style={S.fieldValue}>{data.title}</span>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <div style={S.fieldRow}>
          <span style={S.fieldLabel}>Slides</span>
          <span style={S.fieldValue}>{slides.length}</span>
        </div>
        {slides.length > 2 && (
          <button style={S.expandBtn} onClick={() => setExpanded(!expanded)}>
            {expanded ? '▾ Collapse' : '▸ Show all slides'}
          </button>
        )}
      </div>
      {visibleSlides.map((slide, idx) => {
        const bullets = slide.bullet_points || slide.bullets || [];
        const body = slide.body || slide.content || '';
        const rawNotes = slide.speaker_notes || slide.notes || '';
        const { imageDesc: notesImageDesc, otherNotes } = extractImageDescription(rawNotes);
        const imageUrl = slide.image_url || slide.image || '';
        const imageDesc = slide.image_suggestion || notesImageDesc;

        return (
          <div key={idx} style={{
            background: '#0f1117',
            border: '1px solid #2d3748',
            borderRadius: 8,
            padding: '12px 14px',
            marginBottom: 10,
            borderLeft: '3px solid #8b5cf6',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
            }}>
              <span style={{
                background: 'rgba(139,92,246,0.15)', color: '#8b5cf6',
                fontSize: 11, fontWeight: 700, padding: '2px 8px',
                borderRadius: 12, flexShrink: 0,
              }}>
                {slide.slide_number || idx + 1}
              </span>
              <span style={{ fontWeight: 600, color: '#e2e8f0', fontSize: 13 }}>
                {slide.title || `Slide ${idx + 1}`}
              </span>
            </div>
            {bullets.length > 0 && (
              <ul style={{ margin: '4px 0 4px 16px', padding: 0, color: '#c8d1dc', fontSize: 12, lineHeight: 1.7 }}>
                {bullets.map((b, bi) => (
                  <li key={bi} style={{ marginBottom: 2 }}>
                    {typeof b === 'string' ? b : b.text || JSON.stringify(b)}
                  </li>
                ))}
              </ul>
            )}
            {!bullets.length && body && (
              <div style={{ color: '#94a3b8', fontSize: 12, lineHeight: 1.6 }}>{body}</div>
            )}
            {imageUrl && (
              <div style={{ marginTop: 8 }}>
                <img
                  src={imageUrl}
                  alt={slide.title || 'Slide visual'}
                  style={{ maxWidth: '100%', borderRadius: 6, border: '1px solid #2d3748' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
            {imageDesc && !imageUrl && <SlideImagePlaceholder description={imageDesc} />}
            {otherNotes && (
              <div style={{ color: '#64748b', fontSize: 11, fontStyle: 'italic', marginTop: 6 }}>
                Notes: {typeof otherNotes === 'string' ? (otherNotes.length > 200 ? otherNotes.slice(0, 200) + '…' : otherNotes) : ''}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CurriculumTrackBody({ data, editing, draft, setDraft }) {
  if (editing) {
    return (
      <div>
        <div style={S.fieldRow}>
          <span style={S.fieldLabel}>Title</span>
          <input style={S.editInput} value={draft.title || ''} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
        </div>
        <div style={S.fieldRow}>
          <span style={S.fieldLabel}>Description</span>
          <textarea style={S.editTextarea} value={draft.description || ''} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
        </div>
      </div>
    );
  }
  const modules = data?.modules || [];
  return (
    <div>
      <div style={S.fieldRow}>
        <span style={S.fieldLabel}>Title</span>
        <span style={S.fieldValue}>{data?.title || '—'}</span>
      </div>
      <div style={S.fieldRow}>
        <span style={S.fieldLabel}>Description</span>
        <span style={S.fieldValue}>{data?.description || '—'}</span>
      </div>
      <div style={S.fieldRow}>
        <span style={S.fieldLabel}>Modules</span>
        <span style={S.fieldValue}>{modules.length}</span>
      </div>
    </div>
  );
}

function VideoBody({ data, editing, draft, setDraft }) {
  const [expanded, setExpanded] = useState(false);
  if (editing) {
    return (
      <div>
        <div style={S.fieldRow}><span style={S.fieldLabel}>Title</span><input style={S.editInput} value={draft.title || ''} onChange={(e) => setDraft({ ...draft, title: e.target.value })} /></div>
        <div style={S.fieldRow}><span style={S.fieldLabel}>Description</span><textarea style={S.editTextarea} value={draft.description || ''} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></div>
        <div style={S.fieldRow}><span style={S.fieldLabel}>Narration</span><textarea style={{ ...S.editTextarea, minHeight: 120 }} value={draft.narration_script || ''} onChange={(e) => setDraft({ ...draft, narration_script: e.target.value })} /></div>
      </div>
    );
  }
  const script = data?.narration_script || '';
  const wordCount = script ? script.split(/\s+/).filter(Boolean).length : 0;
  return (
    <div>
      <div style={S.fieldRow}><span style={S.fieldLabel}>Title</span><span style={S.fieldValue}>{data?.title || '—'}</span></div>
      {data?.description && <div style={S.fieldRow}><span style={S.fieldLabel}>Description</span><span style={S.fieldValue}>{data.description}</span></div>}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        {data?.difficulty && <div style={S.fieldRow}><span style={S.fieldLabel}>Difficulty</span><span style={S.difficultyBadge(data.difficulty)}>{data.difficulty}</span></div>}
        {wordCount > 0 && <div style={S.fieldRow}><span style={S.fieldLabel}>Word Count</span><span style={S.fieldValue}>{wordCount.toLocaleString()}</span></div>}
      </div>
      {script && (
        <div>
          <div style={S.preview}>{expanded ? script : script.slice(0, 400) + (script.length > 400 ? '…' : '')}</div>
          {script.length > 400 && <button style={S.expandBtn} onClick={() => setExpanded(!expanded)}>{expanded ? '▾ Collapse' : '▸ Show full narration'}</button>}
        </div>
      )}
    </div>
  );
}

function QuizPanelBody({ data, editing, draft, setDraft }) {
  const [expanded, setExpanded] = useState(false);
  if (editing) {
    return (
      <div>
        <div style={S.fieldRow}><span style={S.fieldLabel}>Title</span><input style={S.editInput} value={draft.title || ''} onChange={(e) => setDraft({ ...draft, title: e.target.value })} /></div>
        <div style={S.fieldRow}><span style={S.fieldLabel}>Description</span><textarea style={S.editTextarea} value={draft.description || ''} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></div>
      </div>
    );
  }
  const questions = data?.questions || [];
  return (
    <div>
      <div style={S.fieldRow}><span style={S.fieldLabel}>Title</span><span style={S.fieldValue}>{data?.title || '—'}</span></div>
      {data?.description && <div style={S.fieldRow}><span style={S.fieldLabel}>Description</span><span style={S.fieldValue}>{data.description}</span></div>}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        {data?.difficulty && <div style={S.fieldRow}><span style={S.fieldLabel}>Difficulty</span><span style={S.difficultyBadge(data.difficulty)}>{data.difficulty}</span></div>}
        <div style={S.fieldRow}><span style={S.fieldLabel}>Questions</span><span style={S.fieldValue}>{questions.length}</span></div>
      </div>
      {questions.length > 0 && (
        <div>
          <button style={S.expandBtn} onClick={() => setExpanded(!expanded)}>
            {expanded ? '▾ Hide questions' : `▸ Show ${questions.length} question(s)`}
          </button>
          {expanded && questions.map((q, qi) => (
            <div key={qi} style={{ ...S.preview, marginTop: 8 }}>
              <div style={{ fontWeight: 600, color: '#e2e8f0', marginBottom: 6 }}>Q{qi + 1}: {q.question}</div>
              {(q.options || []).map((opt, oi) => {
                const isCorrect = q.correct_answer === oi;
                return (
                  <div key={oi} style={S.choiceRow(isCorrect)}>
                    <div style={S.choiceMarker(isCorrect)}>{isCorrect ? '✓' : ''}</div>
                    <span>{opt}</span>
                  </div>
                );
              })}
              {q.explanation && <div style={{ marginTop: 4, fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>{q.explanation}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AILabBody({ data, editing, draft, setDraft }) {
  const [expanded, setExpanded] = useState(false);
  if (editing) {
    return (
      <div>
        <div style={S.fieldRow}><span style={S.fieldLabel}>Title</span><input style={S.editInput} value={draft.title || ''} onChange={(e) => setDraft({ ...draft, title: e.target.value })} /></div>
        <div style={S.fieldRow}><span style={S.fieldLabel}>Scenario</span><textarea style={{ ...S.editTextarea, minHeight: 100 }} value={draft.scenario || ''} onChange={(e) => setDraft({ ...draft, scenario: e.target.value })} /></div>
        <div style={S.fieldRow}><span style={S.fieldLabel}>Challenge</span><textarea style={S.editTextarea} value={draft.challenge_brief || ''} onChange={(e) => setDraft({ ...draft, challenge_brief: e.target.value })} /></div>
      </div>
    );
  }
  const steps = data?.steps_and_instructions || [];
  const fields = data?.editable_fields || [];
  return (
    <div>
      <div style={S.fieldRow}><span style={S.fieldLabel}>Title</span><span style={S.fieldValue}>{data?.title || '—'}</span></div>
      {data?.description && <div style={S.fieldRow}><span style={S.fieldLabel}>Description</span><span style={S.fieldValue}>{data.description}</span></div>}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        {data?.difficulty && <div style={S.fieldRow}><span style={S.fieldLabel}>Difficulty</span><span style={S.difficultyBadge(data.difficulty)}>{data.difficulty}</span></div>}
        <div style={S.fieldRow}><span style={S.fieldLabel}>Steps</span><span style={S.fieldValue}>{steps.length}</span></div>
        <div style={S.fieldRow}><span style={S.fieldLabel}>Editable Fields</span><span style={S.fieldValue}>{fields.length}</span></div>
      </div>
      {data?.scenario && (
        <div>
          <div style={S.preview}>{expanded ? data.scenario : data.scenario.slice(0, 300) + (data.scenario.length > 300 ? '…' : '')}</div>
          {data.scenario.length > 300 && <button style={S.expandBtn} onClick={() => setExpanded(!expanded)}>{expanded ? '▾ Collapse' : '▸ Show full scenario'}</button>}
        </div>
      )}
      {fields.length > 0 && (
        <div style={{ marginTop: 8 }}>
          {fields.map((f, i) => (
            <div key={i} style={S.scenarioListItem}>
              <span style={{ fontWeight: 600, color: '#94a3b8', fontSize: 12 }}>{f.label || f.field_name}</span>
              <span style={{ fontSize: 12, color: '#64748b' }}>{(f.default_value || '').slice(0, 80)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReflectionBody({ data, editing, draft, setDraft }) {
  if (editing) {
    return (
      <div>
        <div style={S.fieldRow}><span style={S.fieldLabel}>Title</span><input style={S.editInput} value={draft.title || ''} onChange={(e) => setDraft({ ...draft, title: e.target.value })} /></div>
      </div>
    );
  }
  const prompts = data?.prompts || [];
  return (
    <div>
      <div style={S.fieldRow}><span style={S.fieldLabel}>Title</span><span style={S.fieldValue}>{data?.title || '—'}</span></div>
      {prompts.length > 0 && (
        <div style={{ marginTop: 6 }}>
          {prompts.map((p, i) => (
            <div key={i} style={{ ...S.scenarioListItem, fontStyle: 'italic' }}>💭 {typeof p === 'string' ? p : p.prompt || JSON.stringify(p)}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function CuratedResourceBody({ data }) {
  const resources = data?.resources || data?.items || [];
  return (
    <div>
      <div style={S.fieldRow}><span style={S.fieldLabel}>Title</span><span style={S.fieldValue}>{data?.title || '—'}</span></div>
      {resources.length > 0 && (
        <div style={{ marginTop: 6 }}>
          {resources.map((r, i) => (
            <div key={i} style={S.scenarioListItem}>
              <span>📎</span>
              <span>{r.title || r.name || r.url || `Resource ${i + 1}`}</span>
              {r.url && <a href={r.url} target="_blank" rel="noreferrer" style={{ color: '#4f8ef7', fontSize: 12 }}>↗</a>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PromptCanvasBody({ data, editing, draft, setDraft }) {
  if (editing) {
    return (
      <div>
        <div style={S.fieldRow}><span style={S.fieldLabel}>Title</span><input style={S.editInput} value={draft.title || ''} onChange={(e) => setDraft({ ...draft, title: e.target.value })} /></div>
      </div>
    );
  }
  const exercises = data?.exercises || [];
  return (
    <div>
      <div style={S.fieldRow}><span style={S.fieldLabel}>Title</span><span style={S.fieldValue}>{data?.title || '—'}</span></div>
      {exercises.length > 0 && (
        <div style={{ marginTop: 6 }}>
          {exercises.map((ex, i) => (
            <div key={i} style={S.scenarioListItem}>
              <span style={S.contentTypeBadge(ex.level || 'beginner')}>{ex.level || `Level ${i + 1}`}</span>
              <span>{ex.title || ex.task || `Exercise ${i + 1}`}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ContentPanelBody({ data, editing, draft, setDraft }) {
  const [expanded, setExpanded] = useState(false);
  if (editing) {
    return (
      <div>
        <div style={S.fieldRow}><span style={S.fieldLabel}>Title</span><input style={S.editInput} value={draft.title || ''} onChange={(e) => setDraft({ ...draft, title: e.target.value })} /></div>
        <div style={S.fieldRow}><span style={S.fieldLabel}>Content</span><textarea style={{ ...S.editTextarea, minHeight: 100 }} value={draft.content || draft.body || ''} onChange={(e) => setDraft({ ...draft, content: e.target.value })} /></div>
      </div>
    );
  }
  const body = data?.content || data?.body || data?.sections?.map((s) => s.content || s.body).join('\n\n') || '';
  return (
    <div>
      <div style={S.fieldRow}><span style={S.fieldLabel}>Title</span><span style={S.fieldValue}>{data?.title || '—'}</span></div>
      {body && (
        <div>
          <div style={S.preview}>{expanded ? body : body.slice(0, 400) + (body.length > 400 ? '…' : '')}</div>
          {body.length > 400 && <button style={S.expandBtn} onClick={() => setExpanded(!expanded)}>{expanded ? '▾ Collapse' : '▸ Show full content'}</button>}
        </div>
      )}
    </div>
  );
}

function PlayBody({ data, editing, draft, setDraft }) {
  const [expanded, setExpanded] = useState(false);
  if (editing) {
    return (
      <div>
        <div style={S.fieldRow}><span style={S.fieldLabel}>Title</span><input style={S.editInput} value={draft.display_name || draft.title || ''} onChange={(e) => setDraft({ ...draft, display_name: e.target.value, title: e.target.value })} /></div>
        <div style={S.fieldRow}><span style={S.fieldLabel}>Description</span><textarea style={S.editTextarea} value={draft.description || ''} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></div>
        <div style={S.fieldRow}><span style={S.fieldLabel}>Difficulty</span><input style={S.editInput} value={draft.difficulty || ''} onChange={(e) => setDraft({ ...draft, difficulty: e.target.value })} /></div>
      </div>
    );
  }
  const actors = data?.actors || [];
  const timeline = data?.timeline || [];
  const triggers = data?.triggers || [];
  const meta = data?.domain_metadata || data || {};
  const offenseActors = actors.filter(a => a.team === 'offense');
  const defenseActors = actors.filter(a => a.team === 'defense');

  const routeEntries = [];
  if (timeline.length > 0) {
    const routeEvent = timeline.find(e => (e.event_id || '').includes('route'));
    if (routeEvent && routeEvent.actor_states) {
      Object.entries(routeEvent.actor_states).forEach(([actorId, state]) => {
        if (state.action && state.action !== 'pass_block' && state.action !== 'dropback') {
          const actor = actors.find(a => a.actor_id === actorId);
          const label = actor?.label || actorId;
          routeEntries.push({ label, action: state.action.replace(/_/g, ' ') });
        }
      });
    }
  }

  return (
    <div>
      <div style={S.fieldRow}><span style={S.fieldLabel}>Play</span><span style={S.fieldValue}>{data?.display_name || data?.title || '—'}</span></div>
      {data?.description && <div style={S.fieldRow}><span style={S.fieldLabel}>Description</span><span style={S.fieldValue}>{data.description}</span></div>}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        {data?.difficulty && <div style={S.fieldRow}><span style={S.fieldLabel}>Difficulty</span><span style={S.difficultyBadge(data.difficulty)}>{data.difficulty}</span></div>}
        {meta?.coverage_type && <div style={S.fieldRow}><span style={S.fieldLabel}>Coverage</span><span style={{ ...S.fieldValue, fontWeight: 600 }}>{meta.coverage_type.replace(/_/g, ' ')}</span></div>}
        {data?.learner_role && <div style={S.fieldRow}><span style={S.fieldLabel}>Role</span><span style={S.fieldValue}>{data.learner_role}</span></div>}
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        <div style={S.fieldRow}><span style={S.fieldLabel}>Offense</span><span style={S.fieldValue}>{offenseActors.length} players</span></div>
        <div style={S.fieldRow}><span style={S.fieldLabel}>Defense</span><span style={S.fieldValue}>{defenseActors.length} players</span></div>
        <div style={S.fieldRow}><span style={S.fieldLabel}>Timeline</span><span style={S.fieldValue}>{timeline.length} event(s)</span></div>
        {triggers.length > 0 && <div style={S.fieldRow}><span style={S.fieldLabel}>Triggers</span><span style={S.fieldValue}>{triggers.length}</span></div>}
      </div>
      {routeEntries.length > 0 && (
        <div>
          <button style={S.expandBtn} onClick={() => setExpanded(!expanded)}>
            {expanded ? '▾ Hide routes' : `▸ Show ${routeEntries.length} route(s)`}
          </button>
          {expanded && (
            <div style={{ marginTop: 6 }}>
              {routeEntries.map((r, i) => (
                <div key={i} style={S.scenarioListItem}>
                  <span style={{ fontWeight: 600, color: '#3b82f6', fontSize: 12, minWidth: 32 }}>{r.label}</span>
                  <span style={{ fontSize: 13, color: '#e2e8f0' }}>{r.action}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {timeline.length > 0 && !expanded && (
        <div style={S.preview}>
          {timeline.map(e => e.display_name || e.event_id).join(' → ')}
        </div>
      )}
    </div>
  );
}

function DrillBody({ data, editing, draft, setDraft }) {
  if (editing) {
    return (
      <div>
        <div style={S.fieldRow}><span style={S.fieldLabel}>Title</span><input style={S.editInput} value={draft.display_name || draft.title || ''} onChange={(e) => setDraft({ ...draft, display_name: e.target.value, title: e.target.value })} /></div>
        <div style={S.fieldRow}><span style={S.fieldLabel}>Description</span><textarea style={S.editTextarea} value={draft.description || ''} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></div>
      </div>
    );
  }
  const actors = data?.actors || [];
  const meta = data?.domain_metadata || data || {};
  const technique = meta?.technique || data?.technique || '';
  const highlightedId = meta?.highlighted_actor_id || data?.highlighted_actor_id || '';
  const highlightedActor = actors.find(a => a.actor_id === highlightedId);

  return (
    <div>
      <div style={S.fieldRow}><span style={S.fieldLabel}>Drill</span><span style={S.fieldValue}>{data?.display_name || data?.title || '—'}</span></div>
      {data?.description && <div style={S.fieldRow}><span style={S.fieldLabel}>Description</span><span style={S.fieldValue}>{data.description}</span></div>}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        {technique && (
          <div style={S.fieldRow}>
            <span style={S.fieldLabel}>Technique</span>
            <span style={{ ...S.contentTypeBadge('drill'), fontWeight: 700 }}>{technique.replace(/_/g, ' ')}</span>
          </div>
        )}
        {data?.difficulty && <div style={S.fieldRow}><span style={S.fieldLabel}>Difficulty</span><span style={S.difficultyBadge(data.difficulty)}>{data.difficulty}</span></div>}
        {highlightedActor && (
          <div style={S.fieldRow}>
            <span style={S.fieldLabel}>Highlighted</span>
            <span style={{ ...S.fieldValue, fontWeight: 600, color: '#f59e0b' }}>{highlightedActor.label || highlightedActor.role || highlightedId}</span>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        <div style={S.fieldRow}><span style={S.fieldLabel}>Players</span><span style={S.fieldValue}>{actors.length}</span></div>
        {data?.learner_role && <div style={S.fieldRow}><span style={S.fieldLabel}>Role</span><span style={S.fieldValue}>{data.learner_role}</span></div>}
      </div>
      {meta?.coaching_focus && (
        <div style={S.preview}>{meta.coaching_focus}</div>
      )}
    </div>
  );
}

function FallbackBody({ data }) {
  return (
    <div style={S.preview}>
      {JSON.stringify(data, null, 2)}
    </div>
  );
}

// ── Full-content preview modal ────────────────────────────────────────────────

function FullPreviewModal({ artifact, onClose }) {
  const { artifact_type, display_name, data } = artifact;

  const closeOnEsc = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', closeOnEsc);
    return () => document.removeEventListener('keydown', closeOnEsc);
  }, [closeOnEsc]);

  // ── shared modal sub-styles ──────────────────────────────────────────────────
  const M = {
    section: (accent) => ({
      background: '#1a1f2e', border: '1px solid #2d3748', borderRadius: 10,
      padding: '18px 20px', marginBottom: 16,
      borderLeft: `4px solid ${accent || '#4f8ef7'}`,
    }),
    label: { color: '#94a3b8', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 },
    value: { color: '#c8d1dc', fontSize: 14, lineHeight: 1.75, whiteSpace: 'pre-wrap' },
    title: { fontWeight: 700, color: '#e2e8f0', fontSize: 15, marginBottom: 10 },
    badge: (color) => ({
      display: 'inline-flex', alignItems: 'center',
      background: `${color}22`, color, border: `1px solid ${color}44`,
      fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 12,
    }),
    numberedStep: (color) => ({
      display: 'flex', gap: 10, marginBottom: 10, alignItems: 'flex-start',
    }),
    stepNum: (color) => ({
      background: `${color}22`, color, fontSize: 11, fontWeight: 700,
      padding: '2px 8px', borderRadius: 12, flexShrink: 0, marginTop: 2,
    }),
    divider: { borderTop: '1px solid #2d3748', marginTop: 12, paddingTop: 12 },
    link: { color: '#4f8ef7', textDecoration: 'none', fontSize: 13, wordBreak: 'break-all' },
    emptyNote: { color: '#64748b', fontStyle: 'italic', fontSize: 13 },
  };

  const renderContent = () => {
    if (!data) return <div style={M.emptyNote}>No content available.</div>;

    // ── Slide Deck ────────────────────────────────────────────────────────────
    if (artifact_type === 'slide_deck') {
      const slides = data.slides || [];
      if (!slides.length) return <div style={M.emptyNote}>No slides found in this deck.</div>;
      return slides.map((slide, idx) => {
        const bullets = slide.bullet_points || slide.bullets || [];
        const body = slide.body || slide.content || '';
        const rawNotes = slide.speaker_notes || slide.notes || '';
        const imageUrl = slide.image_url || slide.image || '';
        return (
          <div key={idx} style={M.section('#8b5cf6')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <span style={M.badge('#8b5cf6')}>Slide {slide.slide_number || idx + 1}</span>
              <span style={M.title}>{slide.title || `Slide ${idx + 1}`}</span>
            </div>
            {imageUrl && (
              <img src={imageUrl} alt={slide.title} style={{ maxWidth: '100%', borderRadius: 8, marginBottom: 12, border: '1px solid #2d3748' }} onError={(e) => { e.target.style.display = 'none'; }} />
            )}
            {bullets.length > 0 && (
              <ul style={{ margin: '0 0 10px 20px', padding: 0, color: '#c8d1dc', fontSize: 14, lineHeight: 1.8 }}>
                {bullets.map((b, bi) => <li key={bi}>{typeof b === 'string' ? b : b.text || JSON.stringify(b)}</li>)}
              </ul>
            )}
            {!bullets.length && body && <div style={M.value}>{body}</div>}
            {rawNotes && (
              <div style={M.divider}>
                <div style={M.label}>Speaker notes</div>
                <div style={{ ...M.value, fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>{rawNotes}</div>
              </div>
            )}
          </div>
        );
      });
    }

    // ── Content Panel ─────────────────────────────────────────────────────────
    if (artifact_type === 'content_panel') {
      const sections = data.sections || [];
      const body = data.content || data.body || '';
      return (
        <div>
          {data.title && <div style={{ ...M.title, fontSize: 18, marginBottom: 14 }}>{data.title}</div>}
          {sections.length > 0
            ? sections.map((sec, i) => (
                <div key={i} style={{ marginBottom: 20 }}>
                  {sec.heading && <div style={{ fontWeight: 700, color: '#e2e8f0', fontSize: 15, marginBottom: 6 }}>{sec.heading}</div>}
                  <div style={M.value}>{sec.content || sec.body || ''}</div>
                </div>
              ))
            : <div style={M.value}>{body || <span style={M.emptyNote}>No text content.</span>}</div>
          }
        </div>
      );
    }

    // ── Quiz Panel / Interactive Quiz ─────────────────────────────────────────
    if (artifact_type === 'quiz_panel' || artifact_type === 'interactive_quiz') {
      const questions = data.questions || [];
      if (!questions.length) return <div style={M.emptyNote}>No questions in this quiz.</div>;
      return (
        <div>
          {data.title && <div style={{ ...M.title, fontSize: 18, marginBottom: 14 }}>{data.title}</div>}
          {data.description && <div style={{ ...M.value, marginBottom: 16, color: '#94a3b8' }}>{data.description}</div>}
          {questions.map((q, qi) => (
            <div key={qi} style={M.section('#f59e0b')}>
              <div style={{ fontWeight: 700, color: '#e2e8f0', fontSize: 14, marginBottom: 10 }}>
                Q{qi + 1}: {q.question || q.prompt || q.stem}
              </div>
              {(q.options || q.choices || []).map((opt, oi) => {
                const isCorrect = q.correct_answer === oi || q.correct_index === oi || q.correct_answer === opt || (typeof opt === 'object' && opt.correct);
                return (
                  <div key={oi} style={S.choiceRow(isCorrect)}>
                    <div style={S.choiceMarker(isCorrect)}>{isCorrect ? '✓' : String.fromCharCode(65 + oi)}</div>
                    <span>{typeof opt === 'string' ? opt : opt.text || JSON.stringify(opt)}</span>
                  </div>
                );
              })}
              {q.explanation && (
                <div style={{ ...M.divider, color: '#94a3b8', fontSize: 12, fontStyle: 'italic', lineHeight: 1.6 }}>
                  <strong style={{ color: '#c8d1dc', fontStyle: 'normal' }}>Explanation: </strong>{q.explanation}
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }

    // ── AI Lab ────────────────────────────────────────────────────────────────
    if (artifact_type === 'ai_lab') {
      const steps = data.steps_and_instructions || data.steps || [];
      const fields = data.editable_fields || [];
      const rubric = data.evaluation_rubric || data.rubric || [];
      return (
        <div>
          {data.title && <div style={{ ...M.title, fontSize: 18, marginBottom: 14 }}>{data.title}</div>}
          {data.scenario && (
            <div style={{ ...M.section('#06b6d4'), marginBottom: 16 }}>
              <div style={M.label}>Scenario</div>
              <div style={M.value}>{data.scenario}</div>
            </div>
          )}
          {data.challenge_brief && (
            <div style={{ marginBottom: 14 }}>
              <div style={M.label}>Challenge</div>
              <div style={M.value}>{data.challenge_brief}</div>
            </div>
          )}
          {steps.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={M.label}>Steps</div>
              {steps.map((step, si) => (
                <div key={si} style={M.numberedStep('#06b6d4')}>
                  <span style={M.stepNum('#06b6d4')}>{si + 1}</span>
                  <span style={{ ...M.value, fontSize: 13 }}>{typeof step === 'string' ? step : step.instruction || step.description || JSON.stringify(step)}</span>
                </div>
              ))}
            </div>
          )}
          {fields.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={M.label}>Editable Fields</div>
              {fields.map((f, fi) => (
                <div key={fi} style={{ background: '#0f1117', border: '1px solid #2d3748', borderRadius: 6, padding: '8px 12px', marginBottom: 6 }}>
                  <div style={{ fontWeight: 600, color: '#94a3b8', fontSize: 12, marginBottom: 2 }}>{f.label || f.field_name}</div>
                  <div style={{ color: '#c8d1dc', fontSize: 13 }}>{f.default_value || f.placeholder || ''}</div>
                </div>
              ))}
            </div>
          )}
          {rubric.length > 0 && (
            <div>
              <div style={M.label}>Evaluation Rubric</div>
              {rubric.map((r, ri) => (
                <div key={ri} style={{ display: 'flex', gap: 10, marginBottom: 8, alignItems: 'flex-start' }}>
                  <span style={M.badge('#22c55e')}>{r.score || r.points || ri + 1}pts</span>
                  <span style={{ ...M.value, fontSize: 13 }}>{r.criterion || r.description || JSON.stringify(r)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    // ── Reflection ────────────────────────────────────────────────────────────
    if (artifact_type === 'reflection') {
      const prompts = data.prompts || data.reflection_prompts || [];
      const guidelines = data.formatting_guidelines || data.guidelines || data.instructions || '';
      return (
        <div>
          {data.title && <div style={{ ...M.title, fontSize: 18, marginBottom: 14 }}>{data.title}</div>}
          {data.description && <div style={{ ...M.value, marginBottom: 16, color: '#94a3b8' }}>{data.description}</div>}
          {prompts.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={M.label}>Reflection Prompts</div>
              {prompts.map((p, pi) => (
                <div key={pi} style={{ ...M.section('#ec4899'), marginBottom: 10 }}>
                  <div style={{ fontSize: 18, marginBottom: 6 }}>💭</div>
                  <div style={M.value}>{typeof p === 'string' ? p : p.prompt || p.text || JSON.stringify(p)}</div>
                </div>
              ))}
            </div>
          )}
          {guidelines && (
            <div>
              <div style={M.label}>Formatting Guidelines</div>
              <div style={{ ...M.value, color: '#94a3b8', fontSize: 13 }}>{guidelines}</div>
            </div>
          )}
        </div>
      );
    }

    // ── Curated Resource ──────────────────────────────────────────────────────
    if (artifact_type === 'curated_resource') {
      const resources = data.resources || data.items || [];
      const grouped = data.grouped || {};
      const levels = ['beginner', 'intermediate', 'advanced'];
      const hasGrouped = levels.some((l) => grouped[l]?.length);
      return (
        <div>
          {data.title && <div style={{ ...M.title, fontSize: 18, marginBottom: 14 }}>{data.title}</div>}
          {data.description && <div style={{ ...M.value, marginBottom: 16, color: '#94a3b8' }}>{data.description}</div>}
          {hasGrouped
            ? levels.map((level) => {
                const items = grouped[level] || [];
                if (!items.length) return null;
                const accent = { beginner: '#22c55e', intermediate: '#f59e0b', advanced: '#ef4444' }[level];
                return (
                  <div key={level} style={{ marginBottom: 18 }}>
                    <div style={{ ...M.label, color: accent, marginBottom: 8 }}>{level}</div>
                    {items.map((r, ri) => (
                      <div key={ri} style={{ ...M.section(accent), marginBottom: 10 }}>
                        <div style={{ fontWeight: 700, color: '#e2e8f0', fontSize: 14, marginBottom: 4 }}>{r.title || r.name}</div>
                        {r.description && <div style={{ ...M.value, fontSize: 13, marginBottom: 6, color: '#94a3b8' }}>{r.description}</div>}
                        {r.url && <a href={r.url} target="_blank" rel="noreferrer" style={M.link}>{r.url}</a>}
                        {r.why_chosen && <div style={{ marginTop: 6, fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>{r.why_chosen}</div>}
                      </div>
                    ))}
                  </div>
                );
              })
            : resources.map((r, ri) => (
                <div key={ri} style={{ ...M.section('#6366f1'), marginBottom: 10 }}>
                  <div style={{ fontWeight: 700, color: '#e2e8f0', fontSize: 14, marginBottom: 4 }}>{r.title || r.name || `Resource ${ri + 1}`}</div>
                  {r.description && <div style={{ ...M.value, fontSize: 13, marginBottom: 6, color: '#94a3b8' }}>{r.description}</div>}
                  {r.url && <a href={r.url} target="_blank" rel="noreferrer" style={M.link}>{r.url}</a>}
                  {r.annotation && <div style={{ marginTop: 6, fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>📌 {r.annotation}</div>}
                </div>
              ))
          }
        </div>
      );
    }

    // ── Prompt Canvas ─────────────────────────────────────────────────────────
    if (artifact_type === 'prompt_canvas') {
      const exercises = data.exercises || data.prompts || [];
      return (
        <div>
          {data.title && <div style={{ ...M.title, fontSize: 18, marginBottom: 14 }}>{data.title}</div>}
          {data.description && <div style={{ ...M.value, marginBottom: 16, color: '#94a3b8' }}>{data.description}</div>}
          {exercises.map((ex, ei) => {
            const accent = { beginner: '#22c55e', intermediate: '#f59e0b', advanced: '#ef4444' }[ex.level] || '#4f8ef7';
            return (
              <div key={ei} style={M.section(accent)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <span style={M.badge(accent)}>{ex.level || `Level ${ei + 1}`}</span>
                  {ex.title && <span style={{ fontWeight: 700, color: '#e2e8f0', fontSize: 14 }}>{ex.title}</span>}
                </div>
                {ex.task && <div style={{ ...M.value, marginBottom: 10 }}>{ex.task}</div>}
                {(ex.starter_prompts || ex.prompts || []).length > 0 && (
                  <div>
                    <div style={M.label}>Starter Prompts</div>
                    {(ex.starter_prompts || ex.prompts).map((sp, spi) => (
                      <div key={spi} style={{ background: '#0f1117', border: '1px solid #2d3748', borderRadius: 6, padding: '8px 12px', marginBottom: 6, fontFamily: 'monospace', fontSize: 12, color: '#c8d1dc' }}>
                        {typeof sp === 'string' ? sp : sp.text || JSON.stringify(sp)}
                      </div>
                    ))}
                  </div>
                )}
                {ex.evaluation_criteria && (
                  <div style={M.divider}>
                    <div style={M.label}>Evaluation Criteria</div>
                    <div style={{ ...M.value, fontSize: 12, color: '#94a3b8' }}>{ex.evaluation_criteria}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    // ── Flashcard Scene ───────────────────────────────────────────────────────
    if (artifact_type === 'flashcard_scene' || artifact_type === 'flashcard') {
      return (
        <div>
          {data.skill_id && (
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={M.badge('#f59e0b')}>Skill</span>
              <span style={{ color: '#e2e8f0', fontSize: 13, fontFamily: 'monospace' }}>{data.skill_id}</span>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div style={{ ...M.section('#4f8ef7'), margin: 0 }}>
              <div style={M.label}>Front (Question / Prompt)</div>
              <div style={M.value}>{data.front_content || data.front || <span style={M.emptyNote}>—</span>}</div>
            </div>
            <div style={{ ...M.section('#22c55e'), margin: 0 }}>
              <div style={M.label}>Back (Answer)</div>
              <div style={M.value}>{data.back_content || data.back || <span style={M.emptyNote}>—</span>}</div>
            </div>
          </div>
          {data.key_insight && (
            <div style={{ ...M.section('#f59e0b'), marginBottom: 12 }}>
              <div style={M.label}>Key Insight</div>
              <div style={{ ...M.value, fontSize: 13, fontStyle: 'italic' }}>💡 {data.key_insight}</div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {data.difficulty && <span style={M.badge(DIFFICULTY_COLORS[data.difficulty] || '#94a3b8')}>{data.difficulty}</span>}
            {(data.tags || []).map((t, ti) => <span key={ti} style={M.badge('#6366f1')}>{t}</span>)}
          </div>
        </div>
      );
    }

    // ── Video Scene ───────────────────────────────────────────────────────────
    if (artifact_type === 'video') {
      const url = data.video_url || data.resource_url || data.url || '';
      const isYouTube = url && (url.includes('youtube.com') || url.includes('youtu.be'));
      const embedId = isYouTube ? (url.split('v=')[1]?.split('&')[0] || url.split('/').pop()?.split('?')[0]) : null;
      return (
        <div>
          {data.title && <div style={{ ...M.title, fontSize: 18, marginBottom: 14 }}>{data.title}</div>}
          {data.description && <div style={{ ...M.value, marginBottom: 16, color: '#94a3b8' }}>{data.description}</div>}
          {embedId ? (
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, marginBottom: 16, borderRadius: 8, overflow: 'hidden', border: '1px solid #2d3748' }}>
              <iframe
                src={`https://www.youtube.com/embed/${embedId}`}
                title={data.title || 'Video'}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                frameBorder="0"
                allowFullScreen
              />
            </div>
          ) : url ? (
            <div style={{ ...M.section('#3b82f6'), marginBottom: 16 }}>
              <div style={M.label}>Video URL</div>
              <a href={url} target="_blank" rel="noreferrer" style={M.link}>{url}</a>
            </div>
          ) : (
            <div style={{ ...M.section('#ef4444'), marginBottom: 16 }}>
              <div style={{ color: '#f87171', fontSize: 13 }}>⚠️ No video URL provided — coach must supply a link before this scene can be published.</div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
            {data.access_tier && <span style={M.badge(data.access_tier === 'free' ? '#22c55e' : '#f59e0b')}>{data.access_tier}</span>}
            {data.content_license && <span style={M.badge('#6366f1')}>{data.content_license}</span>}
            {data.difficulty && <span style={M.badge(DIFFICULTY_COLORS[data.difficulty] || '#94a3b8')}>{data.difficulty}</span>}
          </div>
          {data.narration_script && (
            <div>
              <div style={M.label}>Narration Script</div>
              <div style={{ ...M.value, fontSize: 13, color: '#94a3b8', maxHeight: 300, overflowY: 'auto', background: '#0f1117', border: '1px solid #2d3748', borderRadius: 6, padding: '10px 14px' }}>
                {data.narration_script}
              </div>
            </div>
          )}
        </div>
      );
    }

    // ── Module ────────────────────────────────────────────────────────────────
    if (artifact_type === 'module') {
      const scenarios = data.scenarios || data.scenes || [];
      const skills = data.skills_taught || data.skills || [];
      return (
        <div>
          {data.title && <div style={{ ...M.title, fontSize: 18, marginBottom: 14 }}>{data.title}</div>}
          {data.description && <div style={{ ...M.value, marginBottom: 16 }}>{data.description}</div>}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            {data.difficulty && <span style={M.badge(DIFFICULTY_COLORS[data.difficulty] || '#94a3b8')}>{data.difficulty}</span>}
            {data.mastery_gate !== undefined && <span style={M.badge('#22c55e')}>Pass: {data.mastery_gate}%</span>}
            {data.estimated_duration && <span style={M.badge('#6366f1')}>~{data.estimated_duration}</span>}
          </div>
          {skills.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={M.label}>Skills Taught</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                {skills.map((sk, si) => <span key={si} style={M.badge('#f59e0b')}>{typeof sk === 'string' ? sk : sk.name || sk.id || JSON.stringify(sk)}</span>)}
              </div>
            </div>
          )}
          {scenarios.length > 0 && (
            <div>
              <div style={M.label}>Scenes ({scenarios.length})</div>
              {scenarios.map((sc, si) => (
                <div key={si} style={{ background: '#0f1117', border: '1px solid #2d3748', borderRadius: 6, padding: '8px 12px', marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={M.badge(CONTENT_TYPE_COLORS[sc.content_type] || '#6366f1')}>{sc.content_type?.replace(/_/g, ' ') || 'scene'}</span>
                  <span style={{ color: '#e2e8f0', fontSize: 13 }}>{sc.title || sc.scene_id || `Scene ${si + 1}`}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    // ── Curriculum Track ──────────────────────────────────────────────────────
    if (artifact_type === 'curriculum_track') {
      const modules = data.modules || [];
      return (
        <div>
          {data.title && <div style={{ ...M.title, fontSize: 18, marginBottom: 14 }}>{data.title}</div>}
          {data.description && <div style={{ ...M.value, marginBottom: 16 }}>{data.description}</div>}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            {data.difficulty && <span style={M.badge(DIFFICULTY_COLORS[data.difficulty] || '#94a3b8')}>{data.difficulty}</span>}
            {data.estimated_duration && <span style={M.badge('#6366f1')}>~{data.estimated_duration}</span>}
            {modules.length > 0 && <span style={M.badge('#0ea5e9')}>{modules.length} modules</span>}
          </div>
          {modules.map((mod, mi) => (
            <div key={mi} style={{ ...M.section('#0ea5e9'), marginBottom: 10 }}>
              <div style={M.numberedStep('#0ea5e9')}>
                <span style={M.stepNum('#0ea5e9')}>{mi + 1}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: '#e2e8f0', fontSize: 14, marginBottom: 4 }}>{mod.title || mod.module_id || `Module ${mi + 1}`}</div>
                  {mod.description && <div style={{ ...M.value, fontSize: 13, color: '#94a3b8' }}>{mod.description}</div>}
                  {(mod.skills_taught || []).length > 0 && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                      {mod.skills_taught.map((sk, si) => <span key={si} style={M.badge('#f59e0b')}>{typeof sk === 'string' ? sk : sk.name || JSON.stringify(sk)}</span>)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    // ── Question (standalone) ─────────────────────────────────────────────────
    if (artifact_type === 'question') {
      const choices = data.choices || data.options || [];
      const correct = data.correct_answer ?? data.correct_index;
      return (
        <div>
          {data.category_id && <span style={{ ...M.badge('#f59e0b'), marginBottom: 12, display: 'inline-flex' }}>{data.category_id.replace(/_/g, ' ')}</span>}
          <div style={{ ...M.section('#4f8ef7'), marginBottom: 16 }}>
            <div style={M.label}>Prompt / Stimulus</div>
            <div style={M.value}>{data.prompt || data.question || data.stem || <span style={M.emptyNote}>—</span>}</div>
          </div>
          {choices.map((ch, ci) => {
            const label = typeof ch === 'string' ? ch : ch.text || ch.label || JSON.stringify(ch);
            const isCorrect = correct === ci || correct === label || correct === ch.id || ch.correct;
            return (
              <div key={ci} style={S.choiceRow(isCorrect)}>
                <div style={S.choiceMarker(isCorrect)}>{isCorrect ? '✓' : String.fromCharCode(65 + ci)}</div>
                <span>{label}</span>
              </div>
            );
          })}
          {(data.explanation || data.reasoning) && (
            <div style={{ ...M.divider, color: '#94a3b8', fontSize: 13, fontStyle: 'italic' }}>
              <strong style={{ color: '#c8d1dc', fontStyle: 'normal' }}>Explanation: </strong>
              {data.explanation || data.reasoning}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            {data.difficulty && <span style={M.badge(DIFFICULTY_COLORS[data.difficulty] || '#94a3b8')}>{data.difficulty}</span>}
            {data.section_type_id && <span style={M.badge('#6366f1')}>{data.section_type_id.replace(/_/g, ' ')}</span>}
          </div>
        </div>
      );
    }

    // ── Content Block ─────────────────────────────────────────────────────────
    if (artifact_type === 'content_block') {
      const body = data.content || data.body || data.markdown || '';
      return (
        <div>
          {data.block_id && <div style={{ ...M.label, marginBottom: 10, fontFamily: 'monospace' }}>Block: {data.block_id}</div>}
          {data.difficulty && <span style={{ ...M.badge(DIFFICULTY_COLORS[data.difficulty] || '#94a3b8'), marginBottom: 14, display: 'inline-flex' }}>{data.difficulty}</span>}
          <div style={M.value}>{body || <span style={M.emptyNote}>No content.</span>}</div>
        </div>
      );
    }

    // ── Scenario ──────────────────────────────────────────────────────────────
    if (artifact_type === 'scenario') {
      const body = data.content || data.body || data.prompt || data.description || '';
      return (
        <div>
          {data.title && <div style={{ ...M.title, fontSize: 18, marginBottom: 14 }}>{data.title}</div>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
            {data.content_type && <span style={M.badge(CONTENT_TYPE_COLORS[data.content_type] || '#6366f1')}>{data.content_type.replace(/_/g, ' ')}</span>}
            {data.difficulty && <span style={M.badge(DIFFICULTY_COLORS[data.difficulty] || '#94a3b8')}>{data.difficulty}</span>}
          </div>
          {body && <div style={M.value}>{body}</div>}
        </div>
      );
    }

    // ── Play (Football) ───────────────────────────────────────────────────────
    if (artifact_type === 'play') {
      const actors = data.actors || [];
      const timeline = data.timeline || [];
      const meta = data.domain_metadata || data;
      const offense = actors.filter((a) => a.team === 'offense');
      const defense = actors.filter((a) => a.team === 'defense');
      return (
        <div>
          {data.display_name && <div style={{ ...M.title, fontSize: 18, marginBottom: 14 }}>{data.display_name}</div>}
          {data.description && <div style={{ ...M.value, marginBottom: 16 }}>{data.description}</div>}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            {meta.coverage_type && <span style={M.badge('#16a34a')}>{meta.coverage_type.replace(/_/g, ' ')}</span>}
            {data.difficulty && <span style={M.badge(DIFFICULTY_COLORS[data.difficulty] || '#94a3b8')}>{data.difficulty}</span>}
            <span style={M.badge('#3b82f6')}>Off: {offense.length}</span>
            <span style={M.badge('#ef4444')}>Def: {defense.length}</span>
          </div>
          {timeline.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={M.label}>Timeline ({timeline.length} events)</div>
              {timeline.map((ev, ei) => (
                <div key={ei} style={{ ...M.numberedStep('#16a34a'), marginBottom: 8 }}>
                  <span style={M.stepNum('#16a34a')}>{ei + 1}</span>
                  <span style={{ ...M.value, fontSize: 13 }}>{ev.display_name || ev.event_id || JSON.stringify(ev)}</span>
                </div>
              ))}
            </div>
          )}
          {meta.coaching_notes && (
            <div style={M.section('#16a34a')}>
              <div style={M.label}>Coaching Notes</div>
              <div style={M.value}>{meta.coaching_notes}</div>
            </div>
          )}
        </div>
      );
    }

    // ── Drill (Football) ──────────────────────────────────────────────────────
    if (artifact_type === 'drill') {
      const actors = data.actors || [];
      const meta = data.domain_metadata || data;
      return (
        <div>
          {data.display_name && <div style={{ ...M.title, fontSize: 18, marginBottom: 14 }}>{data.display_name}</div>}
          {data.description && <div style={{ ...M.value, marginBottom: 16 }}>{data.description}</div>}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            {meta.technique && <span style={M.badge('#d97706')}>{meta.technique.replace(/_/g, ' ')}</span>}
            {data.difficulty && <span style={M.badge(DIFFICULTY_COLORS[data.difficulty] || '#94a3b8')}>{data.difficulty}</span>}
            {actors.length > 0 && <span style={M.badge('#6366f1')}>{actors.length} players</span>}
          </div>
          {meta.coaching_focus && (
            <div style={M.section('#d97706')}>
              <div style={M.label}>Coaching Focus</div>
              <div style={M.value}>{meta.coaching_focus}</div>
            </div>
          )}
          {meta.coaching_notes && (
            <div style={{ marginTop: 12 }}>
              <div style={M.label}>Notes</div>
              <div style={{ ...M.value, color: '#94a3b8', fontSize: 13 }}>{meta.coaching_notes}</div>
            </div>
          )}
        </div>
      );
    }

    // ── Fallback: formatted JSON (handles any future type gracefully) ──────────
    return (
      <div>
        <div style={{ ...M.label, marginBottom: 10, color: '#f59e0b' }}>⚠ No dedicated preview for type "{artifact_type}" — showing raw data</div>
        <pre style={{ color: '#94a3b8', fontSize: 12, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#0f1117', border: '1px solid #2d3748', borderRadius: 6, padding: '12px 16px' }}>
          {JSON.stringify(data, null, 2)}
        </pre>
      </div>
    );
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '40px 24px', overflowY: 'auto',
    }} onClick={onClose}>
      <div style={{
        background: '#0f1117', borderRadius: 14, border: '1px solid #2d3748',
        width: '100%', maxWidth: 820, boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
        overflow: 'hidden',
      }} onClick={(e) => e.stopPropagation()}>
        {/* Modal header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 24px', borderBottom: '1px solid #2d3748',
          background: '#1a1f2e',
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#e2e8f0', marginBottom: 2 }}>
              {display_name || artifact_type?.replace(/_/g, ' ') || 'Content Preview'}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {artifact_type?.replace(/_/g, ' ')}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
              color: '#ef4444', borderRadius: 6, padding: '6px 12px', cursor: 'pointer',
              fontSize: 13, fontWeight: 600,
            }}
          >
            ✕ Close (Esc)
          </button>
        </div>
        {/* Modal body */}
        <div style={{ padding: '24px', maxHeight: 'calc(90vh - 80px)', overflowY: 'auto' }}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function StructuredArtifactCard({
  artifact,
  onApprove,
  onPublish,
  onReject,
  onEdit,
  onRegenerate,
  readOnly = false,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [rejectFeedback, setRejectFeedback] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const { artifact_id, artifact_type, display_name, data, status } = artifact;
  const isFinalized = status === 'approved' || status === 'rejected' || status === 'imported';
  const icon = artifactIcon(artifact_type);

  function startEditing() {
    setDraft({ ...(data || {}) });
    setEditing(true);
  }

  function cancelEditing() {
    setEditing(false);
    setDraft({});
  }

  function saveEdits() {
    onEdit?.(artifact_id, draft);
    setEditing(false);
    setDraft({});
  }

  function handleReject() {
    if (!showRejectInput) {
      setShowRejectInput(true);
      return;
    }
    onReject?.(artifact_id, rejectFeedback || undefined);
    setShowRejectInput(false);
    setRejectFeedback('');
  }

  const bodyProps = { data, editing, draft, setDraft };
  let BodyComponent;
  switch (artifact_type) {
    case 'module':           BodyComponent = ModuleBody; break;
    case 'scenario':         BodyComponent = ScenarioBody; break;
    case 'question':         BodyComponent = QuestionBody; break;
    case 'content_block':    BodyComponent = ContentBlockBody; break;
    case 'slide_deck':       BodyComponent = SlideDeckBody; break;
    case 'curriculum_track': BodyComponent = CurriculumTrackBody; break;
    case 'video':            BodyComponent = VideoBody; break;
    case 'quiz_panel':       BodyComponent = QuizPanelBody; break;
    case 'ai_lab':           BodyComponent = AILabBody; break;
    case 'reflection':       BodyComponent = ReflectionBody; break;
    case 'curated_resource': BodyComponent = CuratedResourceBody; break;
    case 'prompt_canvas':    BodyComponent = PromptCanvasBody; break;
    case 'content_panel':    BodyComponent = ContentPanelBody; break;
    case 'play':             BodyComponent = PlayBody; break;
    case 'drill':            BodyComponent = DrillBody; break;
    default:                 BodyComponent = FallbackBody;
  }

  return (
    <>
      {showPreview && <FullPreviewModal artifact={artifact} onClose={() => setShowPreview(false)} />}

      <div style={S.card(status)}>
        {/* Header */}
        <div style={S.cardHeader(status)}>
          <div style={S.cardTitle}>
            <span>{icon}</span>
            <span>{display_name || artifact_type?.replace(/_/g, ' ') || 'Artifact'}</span>
            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400, fontFamily: 'monospace' }}>
              {artifact_id ? `#${artifact_id.slice(0, 8)}` : ''}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              style={{
                background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.4)',
                color: '#818cf8', borderRadius: 5, padding: '3px 10px', cursor: 'pointer',
                fontSize: 11, fontWeight: 600,
              }}
              onClick={() => setShowPreview(true)}
              title="View full content in a preview panel"
            >
              👁 Full Preview
            </button>
            <span style={S.statusBadge(status)}>{STATUS_STYLES[status]?.label || status}</span>
          </div>
        </div>

        {/* Body */}
        <div style={S.cardBody}>
          <BodyComponent {...bodyProps} />
        </div>

        {/* Reject feedback input */}
        {showRejectInput && !readOnly && (
          <div style={{ padding: '0 16px 10px', display: 'flex', gap: 8 }}>
            <input
              style={S.editInput}
              placeholder="Optional feedback for rejection…"
              value={rejectFeedback}
              onChange={(e) => setRejectFeedback(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleReject(); }}
              autoFocus
            />
            <button style={S.btn('#ef4444', '#fff', false)} onClick={handleReject}>Reject</button>
            <button style={S.btn('#2d3748', '#94a3b8', true)} onClick={() => { setShowRejectInput(false); setRejectFeedback(''); }}>Cancel</button>
          </div>
        )}

        {/* Actions */}
        {!readOnly && !isFinalized && (
          <div style={S.actions}>
            {editing ? (
              <>
                <button style={S.btn('#4f8ef7', '#fff', false)} onClick={saveEdits}>Save</button>
                <button style={S.btn('#2d3748', '#94a3b8', true)} onClick={cancelEditing}>Cancel</button>
              </>
            ) : (
              <>
                <button
                  style={S.btn('#22c55e', '#fff', false)}
                  onClick={() => onPublish?.(artifact_id)}
                  title="Publish this content to a curriculum module so learners can access it"
                >
                  ▲ Publish to Module
                </button>
                <button
                  style={{ ...S.btn('#22c55e', '#22c55e', true), fontSize: 12 }}
                  onClick={() => onApprove?.(artifact_id)}
                  title="Save to Content Library only (not yet learner-visible)"
                >
                  ✓ Save to Library
                </button>
                <button style={S.btn('#4f8ef7', '#fff', false)} onClick={startEditing}>✎ Edit</button>
                <button style={S.btn('#f59e0b', '#f59e0b', true)} onClick={() => onRegenerate?.(artifact_id)}>↻ Regenerate</button>
                <button style={S.btn('#ef4444', '#ef4444', true)} onClick={handleReject}>✕ Reject</button>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}
